'use client'

import { Suspense } from 'react'
import { useRouter } from 'next/navigation'

import { Button } from '@/components/ui/button'
import type { SessionPosition } from '@/lib/study-session/marker'
import type { BlockOutcome } from '@/lib/study-session/session-plan'

import {
  advanceSession,
  useSessionMarker,
  useSessionMove,
  useStudySessionView,
} from './use-study-session'

/**
 * A block's hand-off: on the finish screen of a page a time-boxed session
 * opened, "Next: block 2 of 3 — 8 review cards" and a Continue that records
 * what this page counted and opens the next block — instead of dropping the
 * student back on the dashboard. After the last block it reads "Finish
 * session" and goes to the wrap-up.
 *
 * Renders nothing unless the URL carries a session marker for the session's
 * CURRENT page: an old tab, or a page opened outside the session, keeps its
 * ordinary finish screen. `outcome` is what the page itself counted ("8 cards
 * reviewed", "6 answered, 5 right") — counts only, never time.
 */
export default function SessionHandoff({
  outcome,
  className = '',
}: {
  outcome: BlockOutcome | null
  className?: string
}) {
  return (
    <Suspense fallback={null}>
      <Handoff outcome={outcome} className={className} />
    </Suspense>
  )
}

function Handoff({ outcome, className }: { outcome: BlockOutcome | null; className: string }) {
  const marker = useSessionMarker()
  // Outside a session the page is untouched: no fetch, no router.
  if (!marker) return null
  return <HandoffCard marker={marker} outcome={outcome} className={className} />
}

function HandoffCard({
  marker,
  outcome,
  className,
}: {
  marker: { id: string; raw: string } & SessionPosition
  outcome: BlockOutcome | null
  className: string
}) {
  const router = useRouter()
  const view = useStudySessionView(marker.id)
  const { busy, error, run } = useSessionMove()

  if (!view || view.status !== 'active' || view.marker !== marker.raw) return null

  async function next() {
    const goto = await run(() => advanceSession(marker.id, marker, outcome))
    if (goto) {
      router.push(goto)
      router.refresh()
    }
  }

  return (
    <div
      role="region"
      aria-label="Your study session"
      data-testid="session-handoff"
      className={`rounded-lg border border-brand/40 bg-brand/5 p-4 ${className}`}
    >
      <p className="text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
        Your {view.minutes}-minute session
      </p>
      <p data-testid="session-handoff-next" className="mt-1 text-sm font-semibold text-foreground">
        {view.nextLabel}
      </p>
      <div className="mt-3 flex justify-end">
        <Button
          size="sm"
          onClick={() => void next()}
          disabled={busy}
          data-track={view.last ? 'study-session.finish' : 'study-session.continue'}
        >
          {busy ? 'One moment…' : view.last ? 'Finish session' : 'Continue'}
        </Button>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-700 dark:text-red-300">
          {error}
        </p>
      )}
    </div>
  )
}
